import { useState, useEffect, useCallback } from 'react';
import { leadsService } from '../services/api';
import { toLocalDateString } from '../utils/helpers';

export function useLeads({ enabled = true } = {}) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
    const [hasMore, setHasMore] = useState(false);

    // Transform a raw lead from API to UI shape
    const transformLead = (lead) => ({
        ...lead,
        clientName: lead.contact
            ? [lead.contact.firstName, lead.contact.lastName].filter(Boolean).join(' ')
            : lead.title || 'Unknown',
        stage: lead.status === 'New Lead' ? 'New' : lead.status,
        amount: lead.finalAmount || lead.quotationAmount || lead.clientBudget || 0,
        inquiryDate: toLocalDateString(lead.createdAt),
        manager: lead.assignee ? lead.assignee.username : 'Unassigned',
        phone: lead.contact?.phone || '',
        source: lead.source?.name || '',
        meta: (lead.campaignName || lead.adsetName || lead.adName) ? {
            campaign_name: lead.campaignName || null,
            adset_name: lead.adsetName || null,
            ad_name: lead.adName || null,
        } : null
    });

    /**
     * fetchLeads — fetches paginated leads from server with optional filters.
     * All filtering (search, status, assignedTo, date) is done server-side.
     * @param {object} params - { status, search, assignedTo, startDate, endDate, page, limit }
     * @param {boolean} append - if true, appends to existing list (load more)
     */
    const fetchLeads = useCallback(async (params = {}, append = false) => {
        setLoading(true);
        try {
            // Default to all leads
            const fetchParams = { limit: 'all', ...params };
            // Remove empty/All values to keep URL clean
            Object.keys(fetchParams).forEach(k => {
                if (fetchParams[k] === '' || fetchParams[k] === 'All' || fetchParams[k] === null || fetchParams[k] === undefined) {
                    delete fetchParams[k];
                }
            });

            const response = await leadsService.getAll(fetchParams);
            const transformedLeads = response.data.map(transformLead);

            if (append) {
                setLeads(prev => [...prev, ...transformedLeads]);
            } else {
                setLeads(transformedLeads);
            }
            setPagination(response.pagination);
            setHasMore(response.pagination?.page < response.pagination?.totalPages);
            setError(null);
        } catch (err) {
            console.error('Error fetching leads:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMoreLeads = useCallback(async (params = {}) => {
        if (hasMore && !loading && pagination) {
            await fetchLeads({ ...params, page: pagination.page + 1 }, true);
        }
    }, [hasMore, loading, pagination, fetchLeads]);

    const [stats, setStats] = useState({ revenue: 0, pipe: 0, total: 0, conversion: 0, wonCount: 0 });
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    const fetchStats = useCallback(async () => {
        try {
            const data = await leadsService.getStats();
            setStats(data);
        } catch (err) {
            console.error('Error fetching leads stats:', err);
        }
    }, []);

    /**
     * fetchAnalytics — fetches aggregated dashboard data from /api/leads/analytics.
     * Returns KPIs, charts, stale leads — all computed server-side. Scales to millions.
     * @param {object} params - { dateRange: 'week'|'month'|'quarter'|'year'|'all' }
     */
    const fetchAnalytics = useCallback(async (params = {}) => {
        setAnalyticsLoading(true);
        try {
            const data = await leadsService.getAnalytics(params);
            setAnalytics(data);
            return data;
        } catch (err) {
            console.error('Error fetching leads analytics:', err);
            return null;
        } finally {
            setAnalyticsLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        if (enabled) {
            fetchLeads();
            fetchStats();
        } else {
            setLoading(false);
        }
    }, [fetchLeads, fetchStats, enabled]);

    const addLead = async (leadData) => {
        try {
            const newLead = await leadsService.create(leadData);
            const transformedLead = transformLead(newLead);
            setLeads(prev => [transformedLead, ...prev]);
            return newLead;
        } catch (err) {
            throw err;
        }
    };

    const updateLead = async (id, leadData) => {
        try {
            const updatedLead = await leadsService.update(id, leadData);
            const transformedLead = transformLead(updatedLead);
            setLeads(prev => prev.map(lead => lead.id === id ? { ...lead, ...transformedLead } : lead));
            return updatedLead;
        } catch (err) {
            throw err;
        }
    };

    const deleteLead = async (id) => {
        try {
            await leadsService.delete(id);
            setLeads(prev => prev.filter(lead => lead.id !== id));
        } catch (err) {
            throw err;
        }
    };

    const deleteAllLeads = async () => {
        try {
            await leadsService.deleteAll();
            await fetchLeads();
        } catch (err) {
            throw err;
        }
    };

    const addLeadNote = async (id, note, type = 'NOTE') => {
        try {
            await leadsService.addNote(id, { content: note, type });
        } catch (err) {
            throw err;
        }
    };

    // Optimistic local-only patch — no API call, instant UI feedback
    const patchLead = (id, patch) => {
        setLeads(prev => prev.map(lead =>
            lead.id === id ? { ...lead, ...patch } : lead
        ));
    };

    return {
        leads,
        loading,
        error,
        pagination,
        hasMore,
        fetchLeads,
        loadMoreLeads,
        addLead,
        updateLead,
        patchLead,
        deleteLead,
        deleteAllLeads,
        addLeadNote,
        stats,
        // Analytics (for dashboard)
        analytics,
        analyticsLoading,
        fetchAnalytics,
    };
}
